import type { ReactNode } from 'react';
import React, { Fragment, useRef } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { FieldProps } from 'formik';
import 'formik';
import { get, kebabCase } from 'lodash';
import { getInputClasses, getValidationStatus } from 'Components/frameworks/formik/inputsUtils';
import { ImageValidationStatus } from 'Components/frameworks/image-validation-status/ImageValidationStatus';
import { ErrorNote } from './ErrorNote';
import { Label } from './Label';
type Props = FieldProps & {
  air360ClassName: string;
  autoComplete?: string;
  customClasses?: string;
  customId?: string;
  dataTestId?: string;
  hideErrorMessage: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  labelAdditionalInfo?: ReactNode;
  placeholder?: string;
  type: string;
};
export const Input = ({
  air360ClassName,
  autoComplete = 'off',
  customClasses = '',
  customId,
  dataTestId,
  field,
  form,
  hideErrorMessage,
  isDisabled = false,
  isRequired = false,
  label,
  labelAdditionalInfo,
  placeholder,
  type,
  ...props
}: Props) => {
  const isComposingRef = useRef(false);

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = (e) => {
    isComposingRef.current = false;
    form.setFieldValue(field.name, e.target.value, true);
  };

  const handleChange = (e) => {
    if (isComposingRef.current) {
      form.setFieldValue(field.name, e.target.value, false);
      return;
    }
    field.onChange(e);
  };

  // Input value defaults to empty string
  const value = get(form.values, field.name, '');
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(
    { ...field, value },
    form,
    isDisabled,
  );
  const errorNoteId = `error-${kebabCase(field.name)}`;
  const classes = getInputClasses({
    hasHeight: true,
    hasValidationStatus: true,
    isDefaultState,
    isDisabled,
    isErrored,
    isValidated,
  });
  const inputStyle = customClasses
    ? classnames(air360ClassName, customClasses)
    : classnames(air360ClassName, classes);
  return (
    <Fragment>
      {!!label && (
        <Label
          className="mx-15 mb-5"
          htmlFor={customId || field.name}
          isErrored={isErrored}
          isRequired={isRequired}
          label={label}
          labelAdditionalInfo={labelAdditionalInfo}
        />
      )}
      <div className="relative isolate flex w-full items-center">
        <input
          aria-describedby={errorNoteId}
          aria-invalid={isErrored}
          aria-required={isRequired}
          autoComplete={autoComplete}
          className={inputStyle}
          data-testid={dataTestId}
          disabled={isDisabled}
          id={customId ? customId : field.name}
          type={type}
          {...(placeholder ? { placeholder } : null)}
          {...field}
          onChange={handleChange}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          // default to '' for prevent react warning on controlled component
          value={value || ''}
          {...props}
        />
        {!isDisabled && (
          <ImageValidationStatus
            className="absolute end-0 mx-20"
            isErrored={isErrored}
            isValidated={isValidated}
          />
        )}
      </div>
      {!hideErrorMessage && (
        <ErrorNote
          className="mx-15"
          isErrored={isErrored}
          errorMessage={get(form.errors, `${field.name}`) as string | undefined}
          errorNoteId={errorNoteId}
        />
      )}
    </Fragment>
  );
};
